import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  Check,
  Close,
  FileDownloadOutlined,
  ErrorOutline,
  InfoOutlined,
  MoreVert,
  OpenInNew,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getPOById,
  getPOByCode,
} from "../../services/poService";
import {
  acknowledgePO,
  getInvoiceListByPO,
  getPODoc,
} from "../../services/invoiceService";
import { paymentDetailsText } from "./utils/payments";
import { convertDate, convertToINR } from "../../helpers/Utils";
import { getBackendErrorMessage } from "../../helpers/errorUtils";
import { useToast } from "./hooks/useToast";
import { useInvoiceV2Flag } from "./hooks/useInvoiceV2Flag";
import { useIsMobile } from "../../hooks/useIsMobile";
import StickyActionBar from "../../components/mobile/StickyActionBar";
import MobileFieldRow from "../../components/mobile/FieldRow";
import MobileCard from "../../components/mobile/MobileCard";
import AddressRows, { addressLines } from "./components/AddressRows";
import CreditNoteRequiredDialog from "./components/CreditNoteRequiredDialog";
import ViewPOModal from "./components/ViewPO";
import OrderStatusBadge from "./components/OrderStatusBadge";
import { ApprovalStatus } from "./constants/statusMapping";
import { TOKENS } from "./constants/figmaTokens";
import { canRejectRevision } from "./utils/creditNote";
import { summariseInvoiceTableData } from "./utils/invoice";
import { poValueWithTax } from "./utils/poValue";

function RevisedStatusPill() {
  return (
    <Box
      sx={{
        bgcolor: TOKENS.tertiaryBlueDefault,
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        pl: "4px",
        pr: "8px",
        py: "4px",
        borderRadius: "4px",
      }}
    >
      <ErrorOutline sx={{ fontSize: 12, color: "#FFFFFF" }} />
      <Typography
        component="span"
        sx={{
          fontSize: 10,
          fontWeight: 500,
          lineHeight: "12px",
          textTransform: "uppercase",
          color: "#FFFFFF",
        }}
      >
        Revised
      </Typography>
    </Box>
  );
}

function FieldRow({ label, value }) {
  return (
    <Box display="flex" gap={3} alignItems="flex-start" width="100%">
      <Typography
        sx={{
          width: 180,
          flexShrink: 0,
          color: TOKENS.onSurfaceSecondary,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: "20px",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flexGrow: 1, color: TOKENS.onSurfaceDefault, fontSize: 14, lineHeight: "20px" }}>
        {value}
      </Box>
    </Box>
  );
}

function SectionHeading({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 16,
        fontWeight: 700,
        lineHeight: "24px",
        color: TOKENS.onSurfaceDefault,
        marginBottom: 2,
      }}
    >
      {children}
    </Typography>
  );
}

// Address card — mirrors fins-fe AddressCard (DetailCards.tsx:226-279):
//   body lines come from `bodyAddress` (display_name + line_one/two/three + city + state)
//   pin + gst are rendered separately (caller chooses source — for Supplier these come from
//   the vendor's billing address + vendor_details.gst_tin; for Delivery, body is
//   shipping_address but pin/gst are from billing_address per fins-fe's pattern).
function AddressBlock({ heading, bodyAddress, pin, gst }) {
  if (!bodyAddress) {
    return (
      <Box>
        <SectionHeading>{heading}</SectionHeading>
        <Typography sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceSecondary }}>
          -
        </Typography>
      </Box>
    );
  }
  return (
    <Box>
      <SectionHeading>{heading}</SectionHeading>
      {addressLines(bodyAddress, pin, gst).map((line, idx) => (
        <Typography
          key={idx}
          sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceDefault }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  );
}

const ITEM_COL_HEADER_SX = {
  fontSize: 12,
  fontWeight: 500,
  textTransform: "uppercase",
  color: TOKENS.onSurfaceSecondary,
};

function approvalBadges(inv) {
  return ["vm_approval_status", "l1_approval_status", "l2_approval_status"]
    .map((field) => ({
      stage: field.split("_")[0].toUpperCase(),
      status: inv[field],
    }))
    .filter(({ status }) => !!status)
    .map(({ stage, status }) => (
      <OrderStatusBadge
        key={stage}
        type={ApprovalStatus[status]?.code}
        label={`${stage}: ${ApprovalStatus[status]?.label || status}`}
      />
    ));
}

function RevisionInvoiceCard({ inv, index, formatDate }) {
  const copyUrl = inv.invoice_image?.pre_signed_url;
  return (
    <MobileCard>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: "20px", overflowWrap: "anywhere" }}>
          {index + 1}. {inv.invoice_no || "—"}
        </Typography>
        <IconButton
          size="small"
          aria-label="Invoice copy"
          onClick={() => window.open(copyUrl, "_blank")}
          disabled={!copyUrl}
          sx={{ color: TOKENS.onSurfaceSecondary }}
        >
          <FileDownloadOutlined sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: "20px" }}>
        {inv.invoice_value != null ? convertToINR(inv.invoice_value) : "—"}
        <Typography component="span" sx={{ fontSize: 12, fontWeight: 400, color: TOKENS.onSurfaceSecondary }}>
          {" · "}
          {formatDate(inv.invoice_date)}
        </Typography>
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap="4px" mt={0.5}>
        {approvalBadges(inv)}
      </Stack>
      <Typography sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary, mt: 0.5 }}>
        Uploaded on {formatDate(inv.created)}
      </Typography>
    </MobileCard>
  );
}

// Mirrors fins-fe DiffCell (details/table/columns.tsx:11-38):
// when revised differs from current, show revised on top + strikethrough current below.
function DiffCell({ current, revised, formatter }) {
  const fmt = formatter || ((v) => (v == null ? "-" : String(v)));
  const isChanged = revised != null && revised !== current;
  if (!isChanged) {
    return <span>{fmt(current)}</span>;
  }
  return (
    <Box display="inline-flex" flexDirection="column">
      <span>{fmt(revised)}</span>
      <span style={{ textDecoration: "line-through", color: TOKENS.onSurfaceSecondary }}>
        {fmt(current)}
      </span>
    </Box>
  );
}

export default function RevisedPOReview() {
  const { po_code } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileViewport = useIsMobile();
  const { isV2 } = useInvoiceV2Flag();
  const isMobile = isV2 && isMobileViewport;
  const statePoId = location.state?.poId;

  const poByIdQuery = useQuery({
    queryKey: ["po-by-id", statePoId],
    queryFn: () => getPOById(statePoId),
    enabled: !!statePoId,
    retry: false,
  });

  const poByCodeQuery = useQuery({
    queryKey: ["po-by-code", po_code],
    queryFn: () => getPOByCode(po_code),
    enabled: !statePoId && !!po_code,
    retry: false,
  });

  const poQuery = statePoId ? poByIdQuery : poByCodeQuery;
  const po = poQuery.data;
  const draft = po?.draft;

  const invoiceListQuery = useQuery({
    queryKey: ["invoice-list-for-revision", po_code],
    queryFn: () => getInvoiceListByPO({ po_code }),
    enabled: !!po_code,
    retry: false,
  });
  const poFromInvoiceList = invoiceListQuery.data?.po_list?.[0];
  // Payments ride on the PO details response (ENG-27224): `payments[]` is the
  // legacy + v2 union and `total_amount_paid` its sum.
  const payments = poFromInvoiceList?.payments ?? [];
  const totalPaid = poFromInvoiceList?.total_amount_paid ?? 0;
  const canReject = canRejectRevision(poFromInvoiceList);

  useEffect(() => {
    if (
      !invoiceListQuery.isLoading &&
      !invoiceListQuery.isError &&
      !canReject
    ) {
      navigate(`/dashboard/invoice-management/${encodeURIComponent(po_code)}`, { replace: true });
    }
  }, [
    invoiceListQuery.isLoading,
    invoiceListQuery.isError,
    canReject,
    po_code,
    navigate,
  ]);

  // Post-tax, like the invoices and payments they are compared with (ENG-28003).
  const currentPO = poValueWithTax(po);
  const revisedPO = poValueWithTax(draft);
  const reason = draft?.meta_data?.reason_for_revision;
  const reasonComments = draft?.meta_data?.comments;
  const reasonText = reason
    ? `${reason}${reasonComments ? `. ${reasonComments}` : ""}`
    : "-";

  const invoices = poFromInvoiceList?.invoices ?? [];
  const {
    totalCount,
    totalInvoicedValue,
    approvedCount,
    approvedInvoiceAmount,
  } = summariseInvoiceTableData(invoices);
  const approvedInvoiceText =
    approvedCount > 0
      ? `${convertToINR(approvedInvoiceAmount)} (${approvedCount} Invoice${approvedCount > 1 ? "s" : ""})`
      : "No invoice";

  const paymentsText = paymentDetailsText(totalPaid, payments.length);

  // Iterate LIVE items as the row set. Draft items are looked up by id to render
  // diffs on per-row fields (matches fins-fe's POItemColumns approach).
  const items = po?.po_items ?? [];
  const draftItemsMap = new Map((draft?.po_items ?? []).map((d) => [d.id, d]));
  const changedItemIds = new Set();
  if (draft?.po_items?.length) {
    items.forEach((it) => {
      const d = draftItemsMap.get(it.id);
      if (!d) return;
      if (
        it.quantity !== d.quantity ||
        it.unit_cost_price !== d.unit_cost_price ||
        it.amount_allocated !== d.amount_allocated
      ) {
        changedItemIds.add(it.id);
      }
    });
  }

  const [cnDialogOpen, setCnDialogOpen] = useState(false);
  const { showToast } = useToast();

  const ackMutation = useMutation({
    mutationFn: ({ status }) => {
      const payload = [
        {
          po_code: po_code,
          po_id: po?.id,
          status,
        },
      ];
      return acknowledgePO({ data: payload });
    },
    onSuccess: (_, vars) => {
      showToast({
        type: "success",
        message:
          vars.status === "approve" ? "Revision accepted." : "Revision rejected.",
      });
      navigate("/dashboard/invoice-management");
    },
    onError: (err) => {
      showToast({
        type: "error",
        message: `Failed: ${getBackendErrorMessage(err, "unknown error")}`,
      });
    },
  });

  const handleReject = () => ackMutation.mutate({ status: "reject" });

  const handleAcceptRevision = () => {
    // Scenario A: no invoice yet, or invoice ≤ revised PO → direct accept.
    // Scenario B: invoice > revised PO → open CN required dialog (vendor must submit CN).
    if (approvedCount === 0 || (approvedInvoiceAmount ?? 0) <= (revisedPO ?? 0)) {
      ackMutation.mutate({ status: "approve" });
    } else {
      setCnDialogOpen(true);
    }
  };

  const handleContinueToCreditNote = () => {
    setCnDialogOpen(false);
    navigate(`/dashboard/invoice-management/po/${encodeURIComponent(po_code)}/credit-note`, {
      state: { poId: po?.id },
    });
  };

  const actionDisabled = ackMutation.isLoading || !po;

  const viewCurrentPoMutation = useMutation({
    mutationFn: () => getPODoc({ po_code, source: "FINS" }),
    onError: (err) => {
      showToast({
        type: "error",
        message: `Could not load PO document: ${getBackendErrorMessage(err, "unknown error")}`,
      });
    },
  });
  const viewRevisedPoMutation = useMutation({
    mutationFn: () =>
      getPODoc({ po_code, source: "FINS", fins_status: "revised" }),
    onError: (err) => {
      showToast({
        type: "error",
        message: `Could not load revised PO document: ${getBackendErrorMessage(err, "unknown error")}`,
      });
    },
  });

  const [viewedPo, setViewedPo] = useState(null);
  const openPoInModal = (mutation, errorLabel) => {
    mutation.mutate(undefined, {
      onSuccess: (data) => {
        if (data?.pre_signed_url) {
          setViewedPo({
            po_code,
            _file: {
              file_name: data.file_name,
              pre_signed_url: data.pre_signed_url,
            },
          });
        } else {
          showToast({
            type: "error",
            message: `${errorLabel} document not available`,
          });
        }
      },
    });
  };
  const handleViewCurrentPO = () =>
    openPoInModal(viewCurrentPoMutation, "Current PO");
  const handleViewRevisedPO = () =>
    openPoInModal(viewRevisedPoMutation, "Revised PO");

  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const downloadPoFromMutation = async (mutation, errorLabel) => {
    try {
      const data = await mutation.mutateAsync();
      if (data?.pre_signed_url) {
        const link = document.createElement("a");
        link.href = data.pre_signed_url;
        link.setAttribute("download", data.file_name || "");
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        showToast({
          type: "error",
          message: `${errorLabel} document not available`,
        });
      }
    } catch {
      // mutation onError handles the toast
    }
  };
  const handleDownloadCurrentPO = () => {
    setDownloadMenuAnchor(null);
    downloadPoFromMutation(viewCurrentPoMutation, "Current PO");
  };
  const handleDownloadRevisedPO = () => {
    setDownloadMenuAnchor(null);
    downloadPoFromMutation(viewRevisedPoMutation, "Revised PO");
  };


  const formatDateOrDash = (d) => (d ? convertDate(d) : "—");

  const poAmountLinkSx = {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "20px",
    color: TOKENS.onSurfaceDefault,
    textDecoration: "underline",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
  };
  const currentPoValue = (
    <Box display="flex" alignItems="center" gap="10px">
      <Typography sx={{ fontSize: 14, lineHeight: "20px" }}>
        {currentPO != null ? convertToINR(currentPO) : "-"}
      </Typography>
      <MuiLink
        component="button"
        aria-label="View current PO"
        onClick={handleViewCurrentPO}
        disabled={viewCurrentPoMutation.isLoading}
        sx={poAmountLinkSx}
      >
        {isMobile ? "View" : "View current PO"}
        <OpenInNew sx={{ fontSize: 20 }} />
      </MuiLink>
    </Box>
  );
  const revisedPoValue = (
    <Box display="flex" alignItems="center" gap="10px">
      <Typography sx={{ fontSize: 14, lineHeight: "20px" }}>
        {revisedPO != null ? convertToINR(revisedPO) : "-"}
      </Typography>
      <MuiLink
        component="button"
        aria-label="View Revised PO"
        onClick={handleViewRevisedPO}
        disabled={viewRevisedPoMutation.isLoading}
        sx={poAmountLinkSx}
      >
        {isMobile ? "View" : "View Revised PO"}
        <OpenInNew sx={{ fontSize: 20 }} />
      </MuiLink>
    </Box>
  );
  const supplierAddress = {
    bodyAddress: po?.vendor_details?.vendor_billing_address,
    pin: po?.vendor_details?.vendor_billing_address?.post_code,
    gst: po?.vendor_details?.gst_tin,
  };
  const deliveryAddress = {
    bodyAddress: po?.shipping_address,
    pin: po?.billing_address?.post_code,
    gst: po?.billing_address?.tax_id,
  };
  const actionButtons = canReject ? (
    <>
      <Button
        variant="contained"
        onClick={handleReject}
        disabled={actionDisabled}
        startIcon={<Close />}
        sx={{
          bgcolor: TOKENS.surfaceVariant,
          color: TOKENS.onSurfaceSecondary,
          boxShadow: "none",
          height: isMobile ? "auto" : 36,
          minHeight: 36,
          textTransform: "none",
          "&:hover": { bgcolor: TOKENS.surfaceVariant },
          "&.Mui-disabled": {
            bgcolor: TOKENS.surfaceVariant,
            color: TOKENS.onSurfaceSecondary,
            opacity: 0.5,
          },
        }}
      >
        Reject
      </Button>
      <Button
        variant="contained"
        onClick={handleAcceptRevision}
        disabled={actionDisabled}
        startIcon={<Check />}
        sx={{
          bgcolor: TOKENS.primaryDefault,
          color: "#FFFFFF",
          boxShadow: "none",
          height: isMobile ? "auto" : 36,
          minHeight: 36,
          textTransform: "none",
          "&:hover": { bgcolor: TOKENS.primaryDefault },
          "&.Mui-disabled": {
            bgcolor: TOKENS.primaryDefault,
            color: "#FFFFFF",
            opacity: 0.5,
          },
        }}
      >
        Accept Revision
      </Button>
    </>
  ) : null;

  return (
    <Box
      sx={
        isMobile
          ? { bgcolor: TOKENS.surfaceBg, display: "flex", flexDirection: "column", height: "calc(100vh - 48px)" }
          : { bgcolor: TOKENS.surfaceBg, minHeight: "100vh" }
      }
    >
      {/* Sub-header bar */}
      <Box
        sx={{
          bgcolor: TOKENS.surfaceDefault,
          borderBottom: `1px solid ${TOKENS.surfaceVariant}`,
          display: "flex",
          alignItems: "center",
          height: isMobile ? "auto" : 60,
          py: isMobile ? "4px" : 0,
          pl: "8px",
          pr: isMobile ? "12px" : "24px",
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap="4px">
          <Box
            data-testid="back-button"
            onClick={() => navigate(-1)}
            sx={{
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              cursor: "pointer",
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
            }}
          >
            <ArrowBack sx={{ fontSize: 24, color: TOKENS.onSurfaceSecondary }} />
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-start" pb="4px">
            <Typography
              sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}
            >
              Orders/
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap="16px">
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 600,
                  lineHeight: "24px",
                  color: TOKENS.onSurfaceDefault,
                }}
              >
                {po_code}
              </Typography>
              <RevisedStatusPill />
            </Box>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box display="flex" alignItems="center" gap="16px">
          {!isMobile && (
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              <Typography
                sx={{ fontSize: 10, lineHeight: "12px", color: TOKENS.onSurfaceSecondary }}
              >
                Revised PO Amount
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: "24px",
                  color: TOKENS.onSurfaceDefault,
                }}
              >
                {revisedPO != null ? convertToINR(revisedPO) : "-"}
              </Typography>
            </Box>
          )}
          <Stack direction="row" spacing={1}>
            {!isMobile && actionButtons}
            <IconButton
              size="small"
              onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: "6px",
                border: `1px solid ${TOKENS.onSurfaceBorder}`,
                bgcolor: TOKENS.surfaceDefault,
                color: TOKENS.onSurfaceDefault,
                "&:hover": {
                  bgcolor: TOKENS.surfaceBg,
                  borderColor: TOKENS.onSurfaceBorder,
                },
              }}
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={downloadMenuAnchor}
              open={!!downloadMenuAnchor}
              onClose={() => setDownloadMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={handleDownloadRevisedPO}>
                Download Revised PO
              </MenuItem>
              <MenuItem onClick={handleDownloadCurrentPO}>
                Download Current PO
              </MenuItem>
            </Menu>
          </Stack>
        </Box>
      </Box>

      {/* Body */}
      <Box
        sx={{
          padding: isMobile ? "12px" : "24px",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? "16px" : "40px",
          ...(isMobile && {
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            "& > *": { flexShrink: 0 },
          }),
        }}
      >
        {/* Info banner — yellow */}
        <Box
          sx={{
            bgcolor: TOKENS.tertiaryYellowVariant2,
            border: `1px solid ${TOKENS.tertiaryYellowVariant1}`,
            borderRadius: "4px",
            px: "16px",
            py: "12px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <InfoOutlined sx={{ fontSize: 20, color: TOKENS.onTertiaryYellowVariant }} />
          <Typography
            sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceDefault, flex: 1 }}
          >
            Please note : Below is the revised purchase order. Please review and accept the
            revised PO.
          </Typography>
        </Box>

        {poQuery.isLoading && (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} /> Loading PO from FINS…
          </Box>
        )}
        {poQuery.isError && (
          <Alert severity="error">
            FINS PO fetch failed: {poQuery.error?.message || "unknown error"}
          </Alert>
        )}

        {/* Revision details card — light blue */}
        {po && (
          <Box
            sx={{
              bgcolor: TOKENS.tertiaryBlueVariant2,
              border: `1px solid ${TOKENS.tertiaryBlueVariant1}`,
              borderRadius: "8px",
              p: isMobile ? "16px" : "24px",
            }}
          >
            <SectionHeading>Purchase order revision details</SectionHeading>
            {isMobile ? (
              <Stack gap={1}>
                <MobileFieldRow label="Current PO Amount" value={currentPoValue} />
                <MobileFieldRow label="Revised PO Amount" value={revisedPoValue} />
                <MobileFieldRow label="Reason for revision" value={reasonText} />
              </Stack>
            ) : (
            <Box display="flex" gap="48px" alignItems="flex-start">
              <Box display="flex" flexDirection="column" gap="16px" width={624}>
                <FieldRow label="Current PO Amount" value={currentPoValue} />
                <FieldRow label="Reason for revision" value={reasonText} />
              </Box>
              <Box display="flex" flexDirection="column" gap="16px" width={624}>
                <FieldRow label="Revised PO Amount" value={revisedPoValue} />
              </Box>
            </Box>
            )}

            {!draft && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No draft on this PO (has_draft = false). Revised values shown as &quot;-&quot;.
              </Alert>
            )}
          </Box>
        )}

        {/* Approved invoice + Payment details — white card */}
        {po && (
          <Card
            sx={{
              bgcolor: TOKENS.surfaceDefault,
              borderRadius: "8px",
              p: isMobile ? "16px" : "24px",
              boxShadow: "none",
            }}
          >
            {isMobile ? (
              <Stack gap={1}>
                <MobileFieldRow label="Approved invoice" value={approvedInvoiceText} />
                <MobileFieldRow label="Payment details" value={paymentsText} />
              </Stack>
            ) : (
            <Box display="flex" gap="48px">
              <Box flex={1}>
                <SectionHeading>Approved invoice</SectionHeading>
                <Typography sx={{ fontSize: 14, lineHeight: "20px" }}>{approvedInvoiceText}</Typography>
              </Box>
              <Box flex={1}>
                <SectionHeading>Payment details</SectionHeading>
                <Typography sx={{ fontSize: 14, lineHeight: "20px" }}>{paymentsText}</Typography>
              </Box>
            </Box>
            )}
          </Card>
        )}

        {/* Supplier + Delivery addresses — white card */}
        {po && (
          <Card
            sx={{
              bgcolor: TOKENS.surfaceDefault,
              borderRadius: "8px",
              p: isMobile ? "16px" : "24px",
              boxShadow: "none",
            }}
          >
            {isMobile ? (
              <Stack gap={1}>
                <MobileFieldRow label="Supplier Address" value={<AddressRows {...supplierAddress} />} />
                <MobileFieldRow label="Delivery Address" value={<AddressRows {...deliveryAddress} />} />
              </Stack>
            ) : (
            <Box display="flex" gap="48px">
              <Box flex={1}>
                <AddressBlock heading="Supplier Address" {...supplierAddress} />
              </Box>
              <Box flex={1}>
                <AddressBlock heading="Delivery Address" {...deliveryAddress} />
              </Box>
            </Box>
            )}
          </Card>
        )}

        {/* Invoice Details — mirrors fins-fe purchaseOrders InvoiceDetailsTable */}
        {po && (
          <Card
            sx={{
              bgcolor: TOKENS.surfaceDefault,
              borderRadius: "8px",
              boxShadow: "none",
              p: isMobile ? "16px" : "24px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px" }}>
              Invoice Details
            </Typography>
            <Typography
              sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceSecondary }}
            >
              Total Invoiced Value:{" "}
              <Box
                component="span"
                sx={{ color: TOKENS.onSurfaceDefault, fontWeight: 500 }}
              >
                {convertToINR(totalInvoicedValue)} of {convertToINR(currentPO ?? 0)}
              </Box>
            </Typography>
            {totalCount === 0 ? (
              <Typography
                sx={{ fontSize: 14, color: TOKENS.onSurfaceSecondary }}
              >
                No invoices yet on this PO.
              </Typography>
            ) : isMobile ? (
              <Stack gap={1}>
                {invoices.map((inv, idx) => (
                  <RevisionInvoiceCard
                    key={inv.invoice_no || idx}
                    inv={inv}
                    index={idx}
                    formatDate={formatDateOrDash}
                  />
                ))}
              </Stack>
            ) : (
              <TableContainer>
                <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...ITEM_COL_HEADER_SX, width: "6%" }}>S.No</TableCell>
                      <TableCell sx={ITEM_COL_HEADER_SX}>Invoice Number</TableCell>
                      <TableCell sx={ITEM_COL_HEADER_SX}>Invoice Date</TableCell>
                      <TableCell sx={ITEM_COL_HEADER_SX}>Invoice Value</TableCell>
                      <TableCell sx={ITEM_COL_HEADER_SX}>Approval Status</TableCell>
                      <TableCell sx={{ ...ITEM_COL_HEADER_SX, width: "10%" }}>Invoice Copy</TableCell>
                      <TableCell sx={ITEM_COL_HEADER_SX}>Uploaded On</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((inv, idx) => (
                      <TableRow key={inv.invoice_no || idx}>
                        <TableCell sx={{ fontSize: 14 }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontSize: 14 }}>
                          {inv.invoice_no || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 14 }}>
                          {formatDateOrDash(inv.invoice_date)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 14 }}>
                          {inv.invoice_value != null
                            ? convertToINR(inv.invoice_value)
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap="4px"
                          >
                            {approvalBadges(inv)}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(
                                inv.invoice_image?.pre_signed_url,
                                "_blank",
                              )
                            }
                            disabled={!inv.invoice_image?.pre_signed_url}
                            sx={{ color: TOKENS.onSurfaceSecondary }}
                          >
                            <FileDownloadOutlined sx={{ fontSize: 20 }} />
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontSize: 14 }}>
                          {formatDateOrDash(inv.created)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        )}

        {/* PO Items table — always renders when PO loaded */}
        {po && (
          <Box>
            <Box display="flex" alignItems="center" gap="8px" mb={2}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px" }}>
                PO Items
              </Typography>
              <Typography
                sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}
              >
                ({items.length})
              </Typography>
            </Box>
            <Card sx={{ borderRadius: "8px", boxShadow: "none", overflow: "hidden" }}>
              {items.length === 0 ? (
                <Box sx={{ p: "24px", textAlign: "center" }}>
                  <Typography
                    sx={{ fontSize: 14, color: TOKENS.onSurfaceSecondary }}
                  >
                    No items on the {draft ? "draft" : "live"} PO.
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 560 }}>
                  <Table size="small" stickyHeader sx={{ minWidth: isMobile ? 1100 : undefined }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={ITEM_COL_HEADER_SX}>SO Item Name</TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          Total Amount with tax
                        </TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>Description</TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>Specification</TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>HSN/SAC Code (GST)</TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          Unit Cost Price
                        </TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          QTY
                        </TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>UOM</TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          Total Amount
                        </TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          CGST
                        </TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          SGST
                        </TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          IGST
                        </TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>PO Remarks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((it, idx) => {
                        // Mirror fins-fe POItemColumns (details/table/columns.tsx):
                        //   - Total Amount with tax = `amount + tax.total` (post-tax cell)
                        //   - Total Amount        = `pre_tax_amount`
                        //   - CGST/SGST shown only when tax.strategy === 'intrastate'
                        //   - IGST shown only when tax.strategy === 'interstate'
                        //   - DiffCell on unit_cost_price + quantity (revised on top, strikethrough current)
                        const tax = it.tax || {};
                        const amountWithTax =
                          it.amount != null ? it.amount + (tax.total || 0) : null;
                        const intra = tax.strategy === "intrastate";
                        const inter = tax.strategy === "interstate";
                        const draftItem = draftItemsMap.get(it.id);
                        const isChanged = changedItemIds.has(it.id);
                        return (
                          <TableRow
                            key={it.id || idx}
                            sx={
                              isChanged
                                ? { bgcolor: TOKENS.tertiaryYellowVariant2 }
                                : undefined
                            }
                          >
                            <TableCell sx={{ fontSize: 14, lineHeight: "20px" }}>
                              {it.name || "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14, fontWeight: 500 }}>
                              {amountWithTax != null ? convertToINR(amountWithTax) : "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14, maxWidth: 240 }}>
                              {it.description || "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14, maxWidth: 200 }}>
                              {it.specification || "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14 }}>{tax.hsn || "-"}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              <DiffCell
                                current={it.unit_cost_price}
                                revised={draftItem?.unit_cost_price}
                                formatter={(v) => (v != null ? convertToINR(v) : "-")}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              <DiffCell
                                current={it.quantity}
                                revised={draftItem?.quantity}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: 14 }}>{it.uom || "-"}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {it.pre_tax_amount != null
                                ? convertToINR(it.pre_tax_amount)
                                : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {intra
                                ? `${convertToINR(tax.cgst_amount || 0)} (${tax.cgst != null ? tax.cgst + "%" : "NA"})`
                                : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {intra
                                ? `${convertToINR(tax.sgst_amount || 0)} (${tax.sgst != null ? tax.sgst + "%" : "NA"})`
                                : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {inter
                                ? `${convertToINR(tax.igst_amount || 0)} (${tax.igst != null ? tax.igst + "%" : "NA"})`
                                : "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14 }}>
                              {it.remarks || "-"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Box>
        )}

        {invoiceListQuery.isError && (
          <Alert severity="error">
            PO details fetch failed: {invoiceListQuery.error?.message || "unknown error"}
          </Alert>
        )}
      </Box>

      {isMobile && actionButtons && <StickyActionBar>{actionButtons}</StickyActionBar>}

      <CreditNoteRequiredDialog
        open={cnDialogOpen}
        isMobile={isMobile}
        onCancel={() => setCnDialogOpen(false)}
        onContinue={handleContinueToCreditNote}
        revisedPOAmount={revisedPO}
        currentPOAmount={currentPO}
        invoiceCount={approvedCount}
        approvedInvoiceAmount={approvedInvoiceAmount}
        paymentCount={payments.length}
        amountPaid={totalPaid}
      />

      <ViewPOModal
        open={viewedPo != null}
        isMobile={isMobile}
        invoice={viewedPo}
        onClose={() => setViewedPo(null)}
        actionHandler={() => {}}
        showActions={false}
      />
    </Box>
  );
}
